import React from 'react';
import { 
  Users, 
  ArrowLeft,
  Settings,
  Shield,
  LogOut,
  FileText,
  Tag
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
  onBackToApp: () => void;
  activeTab: 'professionals' | 'requests' | 'promoCodes';
  onTabChange: (tab: 'professionals' | 'requests' | 'promoCodes') => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, onBackToApp, activeTab, onTabChange }) => {
  const { logout, user } = useAuth();

  console.log('🔍 AdminLayout rendering - activeTab:', activeTab, 'children:', !!children);

  const handleLogout = () => {
    logout();
    // Redirect to login page after logout
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-luma-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBackToApp}
              className="p-2 text-gray-500 hover:text-luma-primary transition-colors"
              title="Back to main app"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="w-8 h-8 bg-luma-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            
            <div>
              <h1 className="text-xl font-bold text-luma-text">SheCare Admin</h1>
              <p className="text-sm text-gray-500">Women's Health Platform Management</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-luma-text">{user?.name || 'Administrator'}</p>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
            </div>
            
            <button className="p-2 text-gray-500 hover:text-luma-primary transition-colors">
              <Settings className="w-5 h-5" />
            </button>
            
            <button 
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-red-500 transition-colors"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-6 py-2">
        <div className="flex items-center space-x-6">
          <button
            onClick={() => onTabChange('requests')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              activeTab === 'requests'
                ? 'bg-luma-primary bg-opacity-10 text-luma-primary'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="text-sm font-medium">Consultation Requests</span>
          </button>
          
          <button
            onClick={() => onTabChange('professionals')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              activeTab === 'professionals'
                ? 'bg-luma-primary bg-opacity-10 text-luma-primary'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Health Professionals</span>
          </button>

          <button
            onClick={() => onTabChange('promoCodes')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
              activeTab === 'promoCodes'
                ? 'bg-luma-primary bg-opacity-10 text-luma-primary'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Tag className="w-4 h-4" />
            <span className="text-sm font-medium">Promo Codes</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;